import { createContext, useContext } from "react";

export enum ReplySort {
  MostRecs = "MOST_RECS",
  MostReplies = "MOST_REPLIES",
  NewestFirst = "NEWEST_FIRST",
  OldestFirst = "OLDEST_FIRST",
}

export enum FanPostFeedMode {
  compact,
  full,
}

export enum Role {
  ADMIN = "ADMIN",
  MODERATOR = "MODERATOR",
  USER = "USER",
  UNKNOWN = "UNKNOWN",
}

export type UserSettings = {
  fanPostFeedMode?: FanPostFeedMode;
  replySort?: ReplySort;
  unfurlMediaEmbeds?: boolean;
};

export type UserSettingsInput = {
  fanPostFeedMode?: FanPostFeedMode;
  replySort?: ReplySort;
  unfurlMediaEmbeds?: boolean;
};

export enum UserStatus {
  None = "None",
  Suspended = "Suspended",
  Banned = "Banned",
}

export type UserRestriction = {
  type: UserStatus;
  allCommunities?: boolean;
  communityID?: string;
  expiresAt?: string;
};

export enum UserMessageType {
  WARNING = "WARNING",
  MESSAGE = "MESSAGE",
}

export type UserMessage = {
  id: string;
  type: UserMessageType;
  message: string;
  createdAt: string;
  acknowledgedAt?: string | null;
};

export type IgnoredUser = {
  id: string;
  user: Pick<User, "id" | "username">;
};

export type CommunityScope = {
  communityID: string;
};

export type User = {
  id: string;
  email: string;
  username?: string;
  displayName?: string;
  displayNameOrUsername?: string;
  role: Role;
  /**
   * Present on MODERATOR viewers after a follow-up fetch.
   * `[]` means unscoped (full access). `null` means the fetch failed.
   * ADMIN and USER viewers leave this unset.
   */
  moderatorScopes?: CommunityScope[] | null;
  createdAt: string;
  avatar?: string;
  communities: string[];
  status: UserStatus;
  restrictions?: UserRestriction[];
  profileID?: string;
  profileURL?: string;
  settings?: UserSettings | null;
  ignoredUsers?: IgnoredUser[];
  activeWarning?: UserMessage | null;
  activeMessages?: UserMessage[];
};

export interface ViewerUserWithToken extends User {
  token?: string | null;
}

export interface CycloneContextType {
  viewer: ViewerUserWithToken | null;
  viewerLoading: boolean;
  viewerBans: UserRestriction[];
  viewerSuspensions: UserRestriction[];
  viewerHasUnacknowledgedWarning: boolean;
  unignoreUser: (id: string) => Promise<void>;
  ignoreUser: (id: string) => Promise<void>;
  updateUserSettings: (id: string, settings: UserSettings) => Promise<void>;
  acknowledgeUserMessage: (id: string) => Promise<void>;
}

const CycloneContext = createContext<CycloneContextType>({
  viewer: null,
  viewerLoading: true,
  viewerBans: [],
  viewerSuspensions: [],
  viewerHasUnacknowledgedWarning: false,
  unignoreUser: async () => {},
  ignoreUser: async () => {},
  updateUserSettings: async () => {},
  acknowledgeUserMessage: async () => {},
});

export function useCyclone(): CycloneContextType {
  const context = useContext(CycloneContext);

  return context;
}

export default CycloneContext;
